import { maskApiKey } from "./setup-llmlite-api-key-and-model.js";
// Test AI provider hoạt động — detect mode từ settings.json, test đúng path:
//   - LLMLite mode (có env.ANTHROPIC_BASE_URL + token): fetch /v1/models + /v1/chat/completions
//   - Subscription mode (no env): spawn `claude --print "say ok"`
//
// v1.2.3 fix: trước đây luôn dùng `claude --print` ngay cả khi LLMLite mode →
// test sai (validate SDK auth chứ không phải LLMLite key). Giờ detect đúng provider.
import { spawnAsyncCapture } from "./spawn-async-capture-with-timeout.js";
import { log } from "./terminal-logger.js";

// Timeout cho HTTP fetch LLMLite — 10s đủ cho list models + chat completion.
const FETCH_TIMEOUT_MS = 10_000;

// Timeout cho subprocess claude --print — 30s an toàn cho cold start.
const CLAUDE_PRINT_TIMEOUT_MS = 30_000;

// Cheap chat completion — max_tokens nhỏ giảm cost (~$0.0001 trên Sonnet).
const TEST_CHAT_MAX_TOKENS = 5;
const TEST_CHAT_PROMPT = "say ok";

export interface TestResult {
  ok: boolean;
  provider: "llmlite" | "subscription" | "anthropic";
  message: string;
}

// Anthropic API version bắt buộc theo spec (giống setup-anthropic module).
const ANTHROPIC_API_VERSION = "2023-06-01";

interface ModelsResponse {
  data?: Array<{ id?: unknown }>;
}

interface ChatResponse {
  choices?: Array<{ message?: { content?: unknown } }>;
  usage?: { total_tokens?: number };
}

// LLMLite test path: /v1/models (verify connectivity + key) + /v1/chat/completions (verify model work).
// Throws với message rõ nếu fail bất kỳ step nào.
async function testLLMLiteProvider(baseUrl: string, token: string, model: string): Promise<void> {
  log.info(`Testing LLMLite provider: ${baseUrl} (key: ${maskApiKey(token)})`);

  // Step 1: List models — verify connectivity + key valid.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const modelsRes = await fetch(`${baseUrl}/v1/models`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });

    if (modelsRes.status === 401 || modelsRes.status === 403) {
      throw new Error(`API key invalid (HTTP ${modelsRes.status}). Re-run: avatar ai setup`);
    }
    if (!modelsRes.ok) {
      throw new Error(`Endpoint /v1/models lỗi (HTTP ${modelsRes.status}).`);
    }

    const modelsJson = (await modelsRes.json()) as ModelsResponse;
    const models = (modelsJson.data || [])
      .map((m) => (typeof m.id === "string" ? m.id : null))
      .filter((id): id is string => id !== null);

    log.success(`Connectivity OK · ${models.length} models available`);
    if (models.length > 0) {
      const preview = models.slice(0, 5).join(", ");
      const more = models.length > 5 ? ` ...+${models.length - 5} more` : "";
      log.dim(`  Models: ${preview}${more}`);
    }

    // Step 2: Cheap chat completion — verify model thật sự work end-to-end.
    log.info(`Testing chat completion với model "${model}"...`);
    const chatRes = await fetch(`${baseUrl}/v1/chat/completions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model,
        messages: [{ role: "user", content: TEST_CHAT_PROMPT }],
        max_tokens: TEST_CHAT_MAX_TOKENS,
      }),
      signal: controller.signal,
    });

    if (!chatRes.ok) {
      const errBody = (await chatRes.text()).slice(0, 200);
      throw new Error(`Chat completion fail (HTTP ${chatRes.status}). ${errBody}`);
    }

    const chatJson = (await chatRes.json()) as ChatResponse;
    const reply =
      typeof chatJson.choices?.[0]?.message?.content === "string"
        ? chatJson.choices[0].message.content
        : "(empty response)";
    const tokens = chatJson.usage?.total_tokens ?? "?";

    log.success(`Response: "${String(reply).trim().slice(0, 100)}"`);
    log.dim(`  Tokens used: ${tokens}`);
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new Error(`Timeout ${FETCH_TIMEOUT_MS / 1000}s. Check mạng / endpoint ${baseUrl}.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Subscription test path: chạy `claude --print` — verify SDK auth + quota.
// Hint cụ thể khi 401 (token stale — pattern từ v1.2.1).
async function testSubscriptionProvider(): Promise<void> {
  log.info("Testing Subscription provider qua `claude --print`...");
  // spawn async: spinner/event-loop tự do + Ctrl-C kill child trong lúc chờ.
  const result = await spawnAsyncCapture("claude", ["--print", TEST_CHAT_PROMPT], {
    timeoutMs: CLAUDE_PRINT_TIMEOUT_MS,
  });

  if (result.timedOut) {
    throw new Error(`Timeout ${CLAUDE_PRINT_TIMEOUT_MS / 1000}s. Check mạng / endpoint.`);
  }
  if (result.status !== 0) {
    const stderr = result.stderr.toLowerCase();
    if (
      stderr.includes("401") ||
      stderr.includes("invalid authentication") ||
      stderr.includes("unauthorized")
    ) {
      throw new Error(
        "Token Claude Code stale (401). Fix: `claude auth logout && claude auth login`.",
      );
    }
    throw new Error(`Test fail (exit ${result.status}). Stderr: ${result.stderr.slice(0, 200)}`);
  }
  log.success(`Response: "${result.stdout.trim().slice(0, 100)}"`);
}

// Anthropic Direct test path: /v1/models (verify key) + /v1/messages (verify model work).
// KHÁC LLMLite: header x-api-key + anthropic-version, body schema khác (system/messages).
async function testAnthropicProvider(
  baseUrl: string,
  apiKey: string,
  model: string,
): Promise<void> {
  log.info(`Testing Anthropic Direct provider: ${baseUrl} (key: ${maskApiKey(apiKey)})`);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    // Step 1: List models — verify connectivity + key valid.
    const modelsRes = await fetch(`${baseUrl}/v1/models`, {
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": ANTHROPIC_API_VERSION,
      },
      signal: controller.signal,
    });

    if (modelsRes.status === 401 || modelsRes.status === 403) {
      throw new Error(`API key invalid (HTTP ${modelsRes.status}). Re-run: avatar ai setup`);
    }
    if (!modelsRes.ok) {
      throw new Error(`Endpoint /v1/models lỗi (HTTP ${modelsRes.status}).`);
    }

    const modelsJson = (await modelsRes.json()) as ModelsResponse;
    const models = (modelsJson.data || [])
      .map((m) => (typeof m.id === "string" ? m.id : null))
      .filter((id): id is string => id !== null);
    log.success(`Connectivity OK · ${models.length} models available`);

    // Step 2: Cheap message — verify model work end-to-end via /v1/messages.
    log.info(`Testing message với model "${model}"...`);
    const msgRes = await fetch(`${baseUrl}/v1/messages`, {
      method: "POST",
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": ANTHROPIC_API_VERSION,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model,
        max_tokens: TEST_CHAT_MAX_TOKENS,
        messages: [{ role: "user", content: TEST_CHAT_PROMPT }],
      }),
      signal: controller.signal,
    });

    if (!msgRes.ok) {
      const errText = (await msgRes.text()).slice(0, 200);
      throw new Error(`Message endpoint fail (HTTP ${msgRes.status}): ${errText}`);
    }
    const msgJson = (await msgRes.json()) as {
      content?: Array<{ text?: unknown }>;
      usage?: { input_tokens?: number; output_tokens?: number };
    };
    const text = (msgJson.content || [])
      .map((c) => (typeof c.text === "string" ? c.text : ""))
      .join("")
      .trim()
      .slice(0, 100);
    log.success(`Response: "${text}"`);
  } finally {
    clearTimeout(timer);
  }
}

// Main entry — detect provider từ settings, run đúng test path.
// Throws với message actionable nếu fail. Caller (avatar ai test) catch + log.error.
//
// v1.6.0: Add Anthropic Direct branch (ANTHROPIC_API_KEY + ANTHROPIC_BASE_URL).
// Order check: anthropic > llmlite > subscription (more specific provider first).
export async function testAiProviderByDetectedMode(
  settings: Record<string, unknown>,
): Promise<TestResult> {
  const env = (settings.env as Record<string, unknown> | undefined) || {};
  const baseUrl = typeof env.ANTHROPIC_BASE_URL === "string" ? env.ANTHROPIC_BASE_URL : undefined;
  const token = typeof env.ANTHROPIC_AUTH_TOKEN === "string" ? env.ANTHROPIC_AUTH_TOKEN : undefined;
  const apiKey = typeof env.ANTHROPIC_API_KEY === "string" ? env.ANTHROPIC_API_KEY : undefined;
  const model = typeof settings.model === "string" ? settings.model : "default";

  if (apiKey && baseUrl) {
    await testAnthropicProvider(baseUrl, apiKey, model);
    return { ok: true, provider: "anthropic", message: "Anthropic Direct provider working" };
  }

  if (baseUrl && token) {
    await testLLMLiteProvider(baseUrl, token, model);
    return { ok: true, provider: "llmlite", message: "LLMLite provider working" };
  }

  await testSubscriptionProvider();
  return { ok: true, provider: "subscription", message: "Subscription provider working" };
}
